import type { BookingSource, FreeCancellationMode } from "./enums.ts";
import { DEFAULT_POLICY } from "./policy/types.ts";
import { evaluate } from "./policy/evaluate.ts";
import { packageLastDay } from "./package-period.ts";
import { DEFAULT_TIMEZONE, localDate, localWeekday, shiftDateKey } from "./time.ts";

/**
 * The member home ("what is next"), as pure functions so the phone and the tests read the same
 * rules. The screen passes in what the API returned; nothing here fetches or formats.
 *
 * The home strip is a rolling run of seven days that starts today, not the business week
 * (`WEEK_START_DAY`): a parent looks forward from today. So the strip is named by its dates, never
 * "This week", which keeps "This week" meaning the same seven days on every screen.
 */

/** How many weeks after this one the strip can page to, at most. */
export const HOME_MAX_PAGE = 3;

/** How long a business cancel stays the main message on the home. */
export const HOME_CANCEL_NOTICE_HOURS = 72;

/** Unused sessions that expire within this many days take over the circle. */
export const HOME_EXPIRY_WARNING_DAYS = 7;

/** A package that can pay for the participant's own group, as the server lists it. */
export type HomePurchase = { purchaseId: string; balance: number; expiresAt: string };

export type HomeParticipant = {
  id: string;
  name: string;
  groupId: string | null;
  /** Usual weekdays, `Date.getDay` numbers (0 is Sunday). */
  usualWeekdays: number[];
  /** Sessions not yet booked. A booking takes its session at once, so booked ones are not in here. */
  sessionsLeft: number;
  /** The first day of the next period, `yyyy-MM-dd`, or null when the package does not renew. */
  renewsOn: string | null;
  /** When the unused sessions of this period expire, if the balance says so. */
  expiresAt: string | null;
  /**
   * The packages that can pay for this participant's group, in the order the booking picks from
   * (`pickPurchaseForBooking`). A family package with no participant shows on every child, with the
   * same id. When present, one-tap booking spends these, not `sessionsLeft`.
   */
  groupPurchases?: HomePurchase[] | null;
};

export type HomeBookingStatus = "confirmed" | "cancelled_by_member" | "cancelled_late" | "cancelled_by_business" | "rescheduled";

export type HomeBooking = {
  bookingId: string;
  participantId: string;
  sessionId: string;
  startsAt: string;
  endsAt: string;
  status: HomeBookingStatus;
  cancelledAt: string | null;
  /** The cut-off on the booking's own policy, from the server. Null when the booking has none. */
  cancelCutoffHours: number | null;
  /** The booking's own late cancel rule, from the server, so the home never states the business-wide one instead. */
  lateCancelRule: "lose_credit" | "return_credit" | "partial";
  lateCancelPartialBps: number | null;
  /** "enrollment" is a fixed weekly slot: a late cancel charges nothing, as the policy engine says. */
  source: BookingSource;
  /** The paying package's free cancellations (null: unlimited) and how many are used, from the server. */
  cancellationAllowance: number | null;
  cancellationsUsed: number;
  /** Which cancels spend them: "any_cancel", or "late_only" (only a late cancel the allowance saves). */
  freeCancellationMode: FreeCancellationMode;
};

export type HomeSlot = {
  sessionId: string;
  groupId: string;
  startsAt: string;
  endsAt: string;
  /** Null when the session has no limit. */
  spotsLeft: number | null;
  bookable: boolean;
  status: "scheduled" | "cancelled" | "moved";
};

/** True only when the session has a limit and no free place. A session with no limit is never full. */
export function isSessionFull(row: { spotsLeft: number | null }): boolean {
  return row.spotsLeft !== null && row.spotsLeft <= 0;
}

export type OpenSlot = { participantId: string; slot: HomeSlot };

export type DotKind = "booked" | "open" | "cancelled";
export type DayDot = { participantId: string; kind: DotKind };
export type HomeDay = { key: string; weekday: number; isToday: boolean; dots: DayDot[] };

export type HomeFocus =
  | { kind: "today"; booking: HomeBooking }
  | { kind: "cancelled"; booking: HomeBooking }
  | { kind: "expiring"; participantId: string; count: number; expiresOn: string; daysLeft: number }
  | { kind: "upcoming"; booking: HomeBooking; daysAway: number; freeUntil: Date | null; freeCancel: boolean }
  | { kind: "locked"; booking: HomeBooking; daysAway: number }
  | { kind: "empty"; participantId: string }
  | { kind: "open"; slots: OpenSlot[] }
  | { kind: "nothing" };

export type HomeAction =
  | { kind: "directions"; booking: HomeBooking }
  | { kind: "book"; slots: OpenSlot[] }
  | { kind: "renew"; participantId: string }
  | { kind: "schedule"; participantId: string | null };

export type LateCancelOutcome = "returned" | "lost" | "not_charged";

type CancelInput = Pick<HomeBooking, "cancelCutoffHours" | "lateCancelRule" | "lateCancelPartialBps" | "source"> &
  Partial<Pick<HomeBooking, "cancellationAllowance" | "cancellationsUsed" | "freeCancellationMode">>;

/**
 * What a member cancel does, answered by the policy engine itself (`evaluate`) with the booking's
 * own policy, source and package allowance, so the home can never say something the cancel sheet
 * contradicts: the session comes back, it is lost, or, for a fixed weekly slot, nothing is charged.
 */
export function cancelOutcome(booking: CancelInput, hoursBeforeStart: number): LateCancelOutcome {
  const startsAt = new Date(Date.UTC(2026, 0, 1, 12));
  const outcome = evaluate({
    policy: {
      ...DEFAULT_POLICY,
      cancelCutoffHours: booking.cancelCutoffHours ?? DEFAULT_POLICY.cancelCutoffHours,
      lateCancelRule: booking.lateCancelRule,
      lateCancelPartialBps: booking.lateCancelPartialBps,
    },
    sessionStartsAt: startsAt,
    now: new Date(startsAt.getTime() - hoursBeforeStart * 3_600_000),
    actor: "member",
    bookingSource: booking.source,
    rescheduleCount: 0,
    monthlyRescheduleCount: 0,
    action: "cancel",
    cancellationAllowance: booking.cancellationAllowance ?? null,
    cancellationsUsed: booking.cancellationsUsed ?? 0,
    freeCancellationMode: booking.freeCancellationMode ?? "any_cancel",
  });
  if (outcome.creditDelta >= 1) return "returned";
  return booking.source === "open" ? "lost" : "not_charged";
}

/** What a member cancel after the cut-off does (one minute before the start). */
export function lateCancelOutcome(booking: CancelInput): LateCancelOutcome {
  return cancelOutcome(booking, 1 / 60);
}

/** Whether a late cancel of a new (open) booking gives the session back. */
export function lateCancelReturns(policy: Pick<HomeBooking, "lateCancelRule" | "lateCancelPartialBps"> & { cancelCutoffHours?: number | null }): boolean {
  return lateCancelOutcome({ cancelCutoffHours: policy.cancelCutoffHours ?? null, lateCancelRule: policy.lateCancelRule, lateCancelPartialBps: policy.lateCancelPartialBps, source: "open" }) === "returned";
}

/** Whole calendar days from `fromKey` to `toKey`, both `yyyy-MM-dd`. */
export function daysBetween(fromKey: string, toKey: string): number {
  return Math.round((Date.parse(`${toKey}T00:00:00Z`) - Date.parse(`${fromKey}T00:00:00Z`)) / 86_400_000);
}

/** The seven day keys of strip page `page`: page 0 starts today. */
export function homeDayKeys(todayKey: string, page = 0): string[] {
  return Array.from({ length: 7 }, (_, index) => shiftDateKey(todayKey, page * 7 + index));
}

/**
 * The last page the strip can show: never before this page, and never past the latest renewal or
 * four weeks out, whichever is first. Next week is always open, because a renewal does not stop a
 * booking after it.
 */
export function homeMaxPage(todayKey: string, participants: Pick<HomeParticipant, "renewsOn">[]): number {
  const renewals = participants.map((row) => row.renewsOn).filter((key): key is string => Boolean(key)).sort();
  const latest = renewals[renewals.length - 1];
  if (!latest) return HOME_MAX_PAGE;
  const pages = Math.ceil(daysBetween(todayKey, latest) / 7);
  return Math.min(HOME_MAX_PAGE, Math.max(1, pages));
}

/**
 * The last moment the booking can change for free. The policy engine treats a change as free
 * while the hours left are at least the cut-off, so this is the start minus the cut-off.
 */
export function freeChangeUntil(booking: Pick<HomeBooking, "startsAt" | "cancelCutoffHours">): Date | null {
  if (booking.cancelCutoffHours === null) return null;
  return new Date(Date.parse(booking.startsAt) - booking.cancelCutoffHours * 3_600_000);
}

/** The confirmed bookings that have not ended yet, soonest first. The member home and the client home read the next one here. */
export function confirmedAhead(bookings: HomeBooking[], now: Date): HomeBooking[] {
  return bookings
    .filter((row) => row.status === "confirmed" && Date.parse(row.endsAt) > now.getTime())
    .sort((a, b) => Date.parse(a.startsAt) - Date.parse(b.startsAt));
}

/**
 * The participant's usual days that have a session of their group with nothing booked, from now
 * until `untilKey` (inclusive). A full or closed session still shows, so the member sees why the
 * day is open; booking it is the screen's job.
 */
export function openUsualSlots(
  participants: HomeParticipant[],
  bookings: HomeBooking[],
  slots: HomeSlot[],
  now: Date,
  untilKey: string,
  timeZone: string = DEFAULT_TIMEZONE,
): OpenSlot[] {
  const out: OpenSlot[] = [];
  for (const participant of participants) {
    if (!participant.groupId || participant.usualWeekdays.length === 0) continue;
    // A day the business cancelled is not offered again: the participant was taken off it.
    const bookedDays = new Set(
      bookings
        .filter((row) => row.participantId === participant.id && (row.status === "confirmed" || row.status === "cancelled_by_business"))
        .map((row) => localDate(new Date(row.startsAt), timeZone)),
    );
    const takenDays = new Set<string>();
    for (const slot of [...slots].sort((a, b) => Date.parse(a.startsAt) - Date.parse(b.startsAt))) {
      if (slot.groupId !== participant.groupId || slot.status !== "scheduled") continue;
      const start = new Date(slot.startsAt);
      if (start.getTime() <= now.getTime()) continue;
      const key = localDate(start, timeZone);
      if (key > untilKey) continue;
      if (!participant.usualWeekdays.includes(localWeekday(start, timeZone))) continue;
      // One usual session a day: a group with two sessions on a Tuesday offers the first one.
      if (bookedDays.has(key) || takenDays.has(key)) continue;
      takenDays.add(key);
      out.push({ participantId: participant.id, slot });
    }
  }
  return out.sort((a, b) => Date.parse(a.slot.startsAt) - Date.parse(b.slot.startsAt));
}

/**
 * The strip: each day with a dot per participant for a booking, an open usual day, or a business
 * cancel. One dot per participant and kind: two sessions of one participant on one day are one
 * mark, not two arcs of the same colour.
 */
export function homeDays(
  dayKeys: string[],
  todayKey: string,
  bookings: HomeBooking[],
  open: OpenSlot[],
  timeZone: string = DEFAULT_TIMEZONE,
): HomeDay[] {
  return dayKeys.map((key) => {
    const dots: DayDot[] = [];
    for (const row of bookings) {
      if (localDate(new Date(row.startsAt), timeZone) !== key) continue;
      if (row.status === "confirmed") dots.push({ participantId: row.participantId, kind: "booked" });
      else if (row.status === "cancelled_by_business") dots.push({ participantId: row.participantId, kind: "cancelled" });
    }
    for (const row of open) {
      if (localDate(new Date(row.slot.startsAt), timeZone) === key) dots.push({ participantId: row.participantId, kind: "open" });
    }
    const unique = dots.filter((dot, index) => dots.findIndex((other) => other.participantId === dot.participantId && other.kind === dot.kind) === index);
    return { key, weekday: new Date(`${key}T00:00:00Z`).getUTCDay(), isToday: key === todayKey, dots: unique };
  });
}

/** The day the unused sessions of this period expire: the balance's own date, else the day before renewal. */
export function expiryKey(participant: HomeParticipant, timeZone: string = DEFAULT_TIMEZONE): string | null {
  if (participant.expiresAt) return packageLastDay(participant.expiresAt, timeZone);
  if (participant.renewsOn) return shiftDateKey(participant.renewsOn, -1);
  return null;
}

/**
 * How far the sessions left go: walk the usual days after the last booked session, one session
 * each, and return the last day that still has one. `reachesExpiry` means sessions are left over
 * when the period ends. Null when there is no routine to walk.
 */
export function coveredUntil(
  participant: HomeParticipant,
  bookings: HomeBooking[],
  now: Date,
  timeZone: string = DEFAULT_TIMEZONE,
): { key: string; reachesExpiry: boolean } | null {
  const todayKey = localDate(now, timeZone);
  const booked = confirmedAhead(bookings.filter((row) => row.participantId === participant.id), now);
  const lastBooked = booked.length > 0 ? localDate(new Date(booked[booked.length - 1]!.startsAt), timeZone) : null;
  if (participant.sessionsLeft <= 0) return lastBooked ? { key: lastBooked, reachesExpiry: false } : null;
  if (participant.usualWeekdays.length === 0) return null;
  const end = expiryKey(participant, timeZone);
  let cursor = lastBooked ?? shiftDateKey(todayKey, -1);
  let left = participant.sessionsLeft;
  let last = lastBooked ?? todayKey;
  for (let step = 0; step < 366 && left > 0; step++) {
    cursor = shiftDateKey(cursor, 1);
    if (end && cursor > end) return { key: end, reachesExpiry: true };
    if (participant.usualWeekdays.includes(new Date(`${cursor}T00:00:00Z`).getUTCDay())) {
      left -= 1;
      last = cursor;
    }
  }
  return { key: last, reachesExpiry: false };
}

/**
 * The one thing the circle says, by priority: a session today, a business cancel, sessions about
 * to expire, the next session (free to change or locked), no sessions left, usual days to book,
 * nothing at all. Business cancels and today's session beat the rest because they need the member
 * now; an expiry beats the next session because waiting loses paid sessions.
 */
export function homeFocus(input: {
  now: Date;
  participants: HomeParticipant[];
  bookings: HomeBooking[];
  open: OpenSlot[];
  timeZone?: string;
}): HomeFocus {
  const timeZone = input.timeZone ?? DEFAULT_TIMEZONE;
  const todayKey = localDate(input.now, timeZone);
  const ahead = confirmedAhead(input.bookings, input.now);
  const next = ahead[0] ?? null;

  if (next && localDate(new Date(next.startsAt), timeZone) === todayKey) return { kind: "today", booking: next };

  const cancelled = input.bookings
    .filter(
      (row) =>
        row.status === "cancelled_by_business" &&
        Date.parse(row.startsAt) > input.now.getTime() &&
        row.cancelledAt !== null &&
        input.now.getTime() - Date.parse(row.cancelledAt) <= HOME_CANCEL_NOTICE_HOURS * 3_600_000,
    )
    .sort((a, b) => Date.parse(a.startsAt) - Date.parse(b.startsAt))[0];
  if (cancelled) return { kind: "cancelled", booking: cancelled };

  const expiring = input.participants
    .map((participant) => {
      const key = expiryKey(participant, timeZone);
      if (!key || participant.sessionsLeft <= 0) return null;
      const daysLeft = daysBetween(todayKey, key);
      if (daysLeft < 0 || daysLeft > HOME_EXPIRY_WARNING_DAYS) return null;
      return { kind: "expiring" as const, participantId: participant.id, count: participant.sessionsLeft, expiresOn: key, daysLeft };
    })
    .filter((row): row is NonNullable<typeof row> => row !== null)
    .sort((a, b) => a.daysLeft - b.daysLeft)[0];
  if (expiring) return expiring;

  if (next) {
    const daysAway = daysBetween(todayKey, localDate(new Date(next.startsAt), timeZone));
    const freeUntil = freeChangeUntil(next);
    // Exactly on the cut-off still counts as before it, as in the policy engine (hours left >= cut-off).
    if (freeUntil && freeUntil.getTime() < input.now.getTime()) return { kind: "locked", booking: next, daysAway };
    // Before the cut-off a cancel is free unless the package has no free cancellations left.
    const hoursLeft = (Date.parse(next.startsAt) - input.now.getTime()) / 3_600_000;
    const freeCancel = cancelOutcome(next, hoursLeft) !== "lost";
    return { kind: "upcoming", booking: next, daysAway, freeUntil, freeCancel };
  }

  const withSessions = input.participants.filter((row) => row.sessionsLeft > 0);
  if (input.participants.length > 0 && withSessions.length === 0) return { kind: "empty", participantId: input.participants[0]!.id };

  const bookable = input.open.filter((row) => withSessions.some((participant) => participant.id === row.participantId));
  if (bookable.length > 0) return { kind: "open", slots: bookable };
  return { kind: "nothing" };
}

/**
 * Whether anything the participant holds can pay for this one slot: a package with sessions left
 * that is still valid on the slot's day (the booking's rule), or, from an older server, any
 * session left. For a single row; a batch uses `bookableSlots`, which also spends as it goes.
 */
export function canPaySlot(participant: HomeParticipant, slot: Pick<HomeSlot, "startsAt">): boolean {
  if (!participant.groupPurchases) return participant.sessionsLeft > 0;
  return participant.groupPurchases.some((purchase) => purchase.balance > 0 && Date.parse(purchase.expiresAt) > Date.parse(slot.startsAt));
}

/**
 * The open usual slots a member can book in one go: bookable, with a place, and paid for. Slots are
 * taken earliest first, each spending the first package the booking itself would pick: one with
 * sessions left that is still valid on the slot's day. A family package is spent once across every
 * child, so two siblings never both count its last session. Without the package list (an older
 * server) each participant's `sessionsLeft` caps their own slots.
 */
export function bookableSlots(open: OpenSlot[], participants: HomeParticipant[]): OpenSlot[] {
  const byId = new Map(participants.map((row) => [row.id, row]));
  const left = new Map(participants.map((row) => [row.id, row.sessionsLeft]));
  const purchaseBalance = new Map<string, number>();
  for (const participant of participants) {
    for (const purchase of participant.groupPurchases ?? []) purchaseBalance.set(purchase.purchaseId, purchase.balance);
  }
  const out: OpenSlot[] = [];
  for (const row of [...open].sort((a, b) => Date.parse(a.slot.startsAt) - Date.parse(b.slot.startsAt))) {
    if (!row.slot.bookable || isSessionFull(row.slot)) continue;
    const participant = byId.get(row.participantId);
    if (!participant) continue;
    if (participant.groupPurchases) {
      const pays = participant.groupPurchases.find(
        (purchase) => (purchaseBalance.get(purchase.purchaseId) ?? 0) > 0 && Date.parse(purchase.expiresAt) > Date.parse(row.slot.startsAt),
      );
      if (!pays) continue;
      purchaseBalance.set(pays.purchaseId, (purchaseBalance.get(pays.purchaseId) ?? 0) - 1);
    } else {
      const remaining = left.get(row.participantId) ?? 0;
      if (remaining <= 0) continue;
      left.set(row.participantId, remaining - 1);
    }
    out.push(row);
  }
  return out;
}

/**
 * The one main button for a focus. `nextWeek` is the open usual slots in the next seven days,
 * `expirySlots` the free places before the expiry for the expiring participant.
 */
export function homeAction(
  focus: HomeFocus,
  context: { participants: HomeParticipant[]; nextWeek: OpenSlot[]; expirySlots: OpenSlot[]; hasVenue: boolean },
): HomeAction | null {
  const nextWeek = bookableSlots(context.nextWeek, context.participants);
  switch (focus.kind) {
    case "today":
      return context.hasVenue ? { kind: "directions", booking: focus.booking } : nextWeek.length > 0 ? { kind: "book", slots: nextWeek.slice(0, 1) } : null;
    case "cancelled": {
      // Another day needs a session to pay for it. With none left, renew first.
      const holder = context.participants.find((row) => row.id === focus.booking.participantId);
      return holder && holder.sessionsLeft <= 0 && focus.booking.source !== "enrollment"
        ? { kind: "renew", participantId: holder.id }
        : { kind: "schedule", participantId: focus.booking.participantId };
    }
    case "expiring": {
      const slots = bookableSlots(context.expirySlots, context.participants).filter((row) => row.participantId === focus.participantId);
      return slots.length > 0 ? { kind: "book", slots } : { kind: "schedule", participantId: focus.participantId };
    }
    case "upcoming":
    case "locked":
      return nextWeek.length > 0 ? { kind: "book", slots: nextWeek.slice(0, 1) } : null;
    case "empty":
      return { kind: "renew", participantId: focus.participantId };
    case "open": {
      const slots = bookableSlots(focus.slots, context.participants);
      return slots.length > 0 ? { kind: "book", slots } : { kind: "schedule", participantId: null };
    }
    case "nothing":
      return { kind: "schedule", participantId: null };
  }
}
